import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Input, InputNumber, Space, Table } from 'antd';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  DeleteOutlined, 
  ClockCircleOutlined, 
  ExpandOutlined, 
  PlayCircleOutlined, 
  PauseCircleOutlined, 
  StepForwardOutlined 
} from '@ant-design/icons';

const getThumbPath = (item) => {
  if (!item) return '';
  if (item.file_type === 'image') return item.file_url || item.file;
  return `https://escreen.stand.ledscreen.rs/media/${item.thumbnail}`;
};

const SortableItem = ({ item, onRemove, onDurationChange, isActive }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: isDragging ? '#334155' : '#1e293b',
    width: '140px',
    marginRight: '10px',
    padding: '4px',
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column',
    border: isActive ? '2px solid #3b82f6' : '1px solid #334155',
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 1
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div 
        style={{ width: '100%', height: '80px', background: '#0f172a', borderRadius: '4px', overflow: 'hidden', position: 'relative' }}
        {...listeners}
      >
        <img src={getThumbPath(item)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'grab' }} />
        {item.resolution && (
            <div style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,0.8)', color: 'white', fontSize: '9px', padding: '1px 4px', borderRadius: '2px', border: '1px solid #475569' }}>
                {item.resolution}
            </div>
        )}
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
          <ClockCircleOutlined style={{ color: 'white', fontSize: '10px' }} />
          <InputNumber 
            size="small" 
            min={1} 
            disabled={item.file_type === 'video'} 
            value={item.duration || 5} 
            onChange={(val) => onDurationChange(item.id, val)} 
            className="white-input-number"
            style={{ width: '45px', fontSize: '10px', background: '#0f172a', color: 'white', border: '1px solid #334155' }} 
          />
        </div>
        <DeleteOutlined style={{ color: '#ef4444', cursor: 'pointer', fontSize: '12px' }} onClick={() => onRemove(item.id)} />
      </div>
    </div>
  );
};

const CreatePlaylistModal = ({ isVisible, onClose, onCreate, mediaFiles, selectedItems, setSelectedItems, loading, editingPlaylist }) => {
  const [playlistName, setPlaylistName] = useState('');
  const [playlistResolution, setPlaylistResolution] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const timerRef = useRef(null);

  const handleNext = useCallback(() => {
    if (selectedItems.length > 0) {
      setCurrentIndex((prev) => (prev + 1) % selectedItems.length);
    }
  }, [selectedItems.length]);

  useEffect(() => {
    if (isVisible) {
      if (editingPlaylist) {
        setPlaylistName(editingPlaylist.name);
        setPlaylistResolution(editingPlaylist.resolution || '');
        const initialItems = (editingPlaylist.items || []).map(item => ({
          ...item.media_details,
          id: `${item.media_details.id}-${Math.random().toString(36).substr(2, 5)}`, 
          duration: item.duration || 5
        }));
        setSelectedItems(initialItems);
      } else {
        setPlaylistName('');
        setPlaylistResolution('');
        setSelectedItems([]);
      }
      setCurrentIndex(0);
      setIsPlaying(false);
    }
    return () => clearTimeout(timerRef.current);
  }, [isVisible, editingPlaylist, setSelectedItems]);

  useEffect(() => {
    if (isPlaying && selectedItems.length > 0) {
      const currentItem = selectedItems[currentIndex];
      if (currentItem.file_type === 'image') {
        const duration = (currentItem.duration || 5) * 1000;
        timerRef.current = setTimeout(handleNext, duration);
      }
    } else {
      clearTimeout(timerRef.current);
    }
    return () => clearTimeout(timerRef.current);
  }, [isPlaying, currentIndex, selectedItems, handleNext]);

  const togglePlay = () => {
    if (selectedItems.length === 0) return;
    setIsPlaying(!isPlaying);
  };

  const handleCreate = () => {
    if (!playlistName.trim()) return;
    const itemsForApi = selectedItems.map(item => ({
      id: parseInt(String(item.id).split('-')[0]),
      duration: parseInt(item.duration) || 5
    }));
    onCreate(playlistName, playlistResolution, itemsForApi);
  };

  const addToPlaylist = (media) => {
    const defaultDuration = media.file_type === 'image' ? 5 : (media.duration || 5);
    const newItem = { 
      ...media, 
      id: `${media.id}-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`, 
      duration: defaultDuration 
    };
    setSelectedItems([...selectedItems, newItem]);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = selectedItems.findIndex(item => item.id === active.id);
      const newIndex = selectedItems.findIndex(item => item.id === over?.id);
      const newItems = [...selectedItems];
      const [movedItem] = newItems.splice(oldIndex, 1);
      newItems.splice(newIndex, 0, movedItem);
      setSelectedItems(newItems);
    }
  };

  const columns = [
    {
      title: 'FILE NAME',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <span style={{ color: '#3b82f6', fontWeight: '500' }}>{text}</span>
      )
    },
    {
      title: 'TYPE',
      dataIndex: 'file_type',
      key: 'type',
      render: (type) => (
        <span style={{ 
          color: type === 'video' ? '#60a5fa' : '#fbbf24', 
          fontWeight: 'bold', 
          fontSize: '11px', 
          textTransform: 'uppercase' 
        }}>
          {type}
        </span>
      )
    },
    {
      title: 'DURATION',
      dataIndex: 'duration',
      key: 'duration',
      render: (text) => <span style={{ color: '#facc15' }}>{text}s</span>
    },
    {
      title: 'BITRATE',
      dataIndex: 'bitrate',
      key: 'bitrate',
      render: (bitrate) => <span style={{ color: '#94a3b8' }}>{bitrate ? `${(bitrate / 1024 / 1024).toFixed(2)} Mbps` : '-'}</span>
    },
    {
      title: 'SIZE',
      dataIndex: 'file_size',
      key: 'size',
      render: (size) => <span style={{ color: '#94a3b8' }}>{size ? `${(size / 1024 / 1024).toFixed(2)} MB` : '-'}</span>
    },
    {
      title: 'RESOLUTION',
      dataIndex: 'resolution',
      key: 'resolution',
      render: (text) => <span style={{ color: '#64748b' }}>{text || '-'}</span>
    },
    {
      title: 'ACTION',
      key: 'action',
      width: 80,
      render: (record) => (
        <Button 
          size="small" 
          type="primary" 
          onClick={() => addToPlaylist(record)}
          style={{ background: '#3b82f6', border: 'none' }}
        >
          Add
        </Button>
      )
    }
  ];

  if (!isVisible) return null;

  return (
    <div className="custom-modal-overlay">
      <div className="playlist-modal-container">
        <div className="playlist-modal-header">
          <span className="playlist-modal-title">{editingPlaylist ? 'Edit Playlist' : 'New Playlist'}</span>
          <span className="playlist-modal-close" onClick={onClose}>&times;</span>
        </div>
        
        <div className="playlist-modal-body">
          <div className="playlist-input-row" style={{ display: 'flex', gap: '15px', marginBottom: '10px' }}>
            <Input 
              placeholder="Playlist Name" 
              value={playlistName} 
              onChange={e => setPlaylistName(e.target.value)} 
              className="dark-input-white-text" 
              style={{ width: '250px' }} 
            />
            <Input 
              placeholder="Resolution (1920x1080)" 
              value={playlistResolution} 
              onChange={e => setPlaylistResolution(e.target.value)} 
              className="dark-input-white-text" 
              style={{ width: '150px' }} 
            />
          </div>

          <div className="playlist-main-layout">
            <div className="playlist-sidebar">
              <div className="media-table-scroll">
                <Table 
                  dataSource={mediaFiles} 
                  columns={columns} 
                  rowKey="id" 
                  pagination={false}
                  size="small"
                  style={{ background: 'transparent' }}
                  className="media-table"
                />
              </div>
            </div>

            <div className="playlist-preview-main">
                <div className="canvas-preview" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  {selectedItems.length > 0 ? (
                    <div style={{ width: '100%', height: '100%', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                      {selectedItems[currentIndex].file_type === 'image' ? (
                        <img 
                         src={getThumbPath(selectedItems[currentIndex])} 
                         style={{ maxWidth: '95%', maxHeight: '90%', objectFit: 'contain' }} 
                         alt="Preview" 
                        />
                      ) : (
                        <video 
                         key={selectedItems[currentIndex].file_url}
                         controls 
                         autoPlay={isPlaying}
                         style={{ maxWidth: '95%', maxHeight: '90%', outline: 'none' }}
                         src={selectedItems[currentIndex].file_url}
                         onEnded={handleNext}
                        />
                      )}
                      <div className="preview-controls">
                        <Space>
                          <Button type="text" icon={isPlaying ? <PauseCircleOutlined style={{fontSize: '28px', color: 'white'}} /> : <PlayCircleOutlined style={{fontSize: '28px', color: 'white'}} />} onClick={togglePlay} />
                          <Button type="text" icon={<StepForwardOutlined style={{fontSize: '22px', color: 'white'}} />} onClick={handleNext} />
                          <span style={{color: 'white', fontSize: '11px', background: 'rgba(0,0,0,0.6)', padding: '2px 8px', borderRadius: '10px', border: '1px solid #334155'}}>
                            {currentIndex + 1} / {selectedItems.length}
                          </span>
                        </Space>
                      </div>
                    </div>
                  ) : <div style={{ color: '#475569' }}>Add files</div>}
                </div>
            </div>
          </div>

          <div className="playlist-timeline">
            <div className="timeline-scroll" style={{ display: 'flex', minWidth: '100%' }}>
              <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={selectedItems.map(i => i.id)} strategy={horizontalListSortingStrategy}>
                  <div style={{ display: 'flex' }}>
                    {selectedItems.map((item, index) => (
                      <SortableItem key={item.id} item={item} isActive={index === currentIndex} onRemove={(id) => setSelectedItems(selectedItems.filter(i => i.id !== id))} onDurationChange={(id, dur) => setSelectedItems(selectedItems.map(i => i.id === id ? {...i, duration: dur} : i))} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          </div>
        </div>

        <div className="playlist-modal-footer">
          <Button onClick={onClose} className="btn-cancel">Cancel</Button>
          <Button type="primary" onClick={handleCreate} loading={loading} className="btn-save">{editingPlaylist ? 'Save' : 'Create'}</Button>
        </div>
      </div>

      <style>{`
        .custom-modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 0, 0, 0.7); backdrop-filter: blur(4px); display: flex; justify-content: center; align-items: center; z-index: 1000; }
        .playlist-modal-container { background-color: #1e293b; width: 85%; height: 85%; display: flex; flex-direction: column; border-radius: 8px; border: 1px solid #334155; box-shadow: 0 0 25px 5px rgba(34, 211, 238, 0.4); overflow: hidden; animation: modalAppear 0.3s ease-out; }
        .playlist-modal-header { padding: 12px 15px; background: #1e293b; border-bottom: 1px solid #334155; display: flex; justify-content: space-between; align-items: center; }
        .playlist-modal-title { color: white; font-weight: bold; font-size: 16px; }
        .playlist-modal-close { color: #94a3b8; font-size: 24px; cursor: pointer; }
        .playlist-modal-body { flex: 1; padding: 15px; display: flex; flex-direction: column; background: #0f172a; gap: 10px; overflow: hidden; }
        .playlist-main-layout { flex: 1; display: flex; gap: 15px; overflow: hidden; }
        
        .playlist-sidebar { width: 40%; border-right: 1px solid #334155; padding-right: 10px; display: flex; flex-direction: column; }
        .media-table-scroll { 
          overflow-y: auto; 
          flex: 1; 
          padding-right: 5px;
        }
        
        .media-table .ant-table { background: transparent !important; }
        .media-table .ant-table-thead > tr > th { background: #1e293b !important; color: #64748b !important; border-bottom: 1px solid #334155 !important; text-transform: uppercase; font-size: 11px; }
        .media-table .ant-table-tbody > tr > td { border-bottom: 1px solid #1e293b !important; color: white !important; }
        .media-table .ant-table-tbody > tr:hover > td { background: rgba(255,255,255,0.02) !important; cursor: pointer; }

        .playlist-preview-main { flex: 1; background: #000; display: flex; align-items: center; justify-content: center; border-radius: 4px; position: relative; overflow: hidden; }
        .preview-controls { position: absolute; bottom: 8px; left: 50%; transform: translateX(-50%); background: rgba(0,0,0,0.7); padding: 4px 12px; border-radius: 30px; border: 1px solid #334155; z-index: 10; display: flex; align-items: center; }
        .playlist-timeline { height: 140px; background: #1e293b; padding: 10px; border-radius: 4px; border: 1px solid #334155; overflow-x: auto; }
        .playlist-modal-footer { padding: 12px 15px; background: #1e293b; border-top: 1px solid #334155; display: flex; justify-content: flex-end; gap: 10px; }
        
        .dark-input-white-text { background: #1e293b !important; color: white !important; border: 1px solid #334155 !important; }
        .dark-input-white-text::placeholder { color: #64748b !important; }
        .dark-input-white-text:focus, .dark-input-white-text:hover { border-color: #3b82f6 !important; }

        .btn-save { background: #3b82f6 !important; border: none !important; box-shadow: 0 0 10px rgba(59, 130, 246, 0.5); }
        .btn-cancel { background: #334155 !important; color: white !important; border: none !important; }
        .white-input-number .ant-input-number-input { color: white !important; }
        
        @keyframes modalAppear { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        
        .media-table-scroll::-webkit-scrollbar { width: 4px; }
        .media-table-scroll::-webkit-scrollbar-thumb { background: #334155; border-radius: 10px; }
      `}</style>
    </div>
  );
};

export default CreatePlaylistModal;